// Decides which pressure alerts share a badge in the tray under the graph.
//
// Alerts falling on the same calendar day (in the field's timezone) are merged
// behind a single badge — repeating the icon across a run of short irrigations
// only clutters the tray. The earliest *visible* alert of each day anchors the
// badge, and its popup lists every alert of that day.
//
// Only alerts are grouped: an ordinary irrigation between two of them keeps its
// own bar and its own hover popup, and never joins the merged list.
//
// Pure and React-free so it can be unit-tested with `node --test`.

const dayjs = require('../../utils/dayjsConfig');
const { pressureAlertLevel } = require('@utils/pressureAlert');

// An event is on screen when it overlaps the visible range — the same test the
// tray's render loop applies, so an anchor is never an event that has scrolled
// out of the plot (which would leave that day with no badge at all).
const isVisible = (event, start, rangeStart, rangeEnd) =>
  !!event && rangeStart < event.endDate && rangeEnd >= start;

// Calendar day of an event in the field's timezone. Grouping by the rendered
// day (rather than by a fixed 24h window) means two alerts either side of
// midnight never merge, however close together they are.
const alertDayKey = (startDate, timezone) =>
  dayjs.unix(startDate).tz(timezone).format('YYYY-MM-DD');

// Returns { [anchorStartDate]: [startDate, ...] } — members chronological, and
// keyed only by the anchor, so a non-anchor alert finds nothing and draws no
// badge of its own.
const groupAlertsByDay = (dailyData, timezone, rangeStart, rangeEnd) => {
  const wfrValues = dailyData?.wfr?.values || {};
  const lengthValues = dailyData?.wfr_length?.values || {};
  const byDay = {};

  Object.keys(lengthValues).forEach((key) => {
    if (!pressureAlertLevel(lengthValues[key]?.pressure_alert)) {
      return;
    }
    const startDate = parseInt(key);
    if (!isVisible(wfrValues[key], startDate, rangeStart, rangeEnd)) {
      return;
    }
    const day = alertDayKey(startDate, timezone);
    byDay[day] = byDay[day] || [];
    byDay[day].push(startDate);
  });

  const anchors = {};
  Object.keys(byDay).forEach((day) => {
    const members = byDay[day].sort((a, b) => a - b);
    anchors[members[0]] = members;
  });
  return anchors;
};

module.exports = {
  groupAlertsByDay,
  alertDayKey,
  isVisible,
};
